import { useMutation } from '@tanstack/react-query';

import { apiFetch } from '@/utils/apiFetch';

interface ApproveOAuth2Params {
  clientId: string;
  redirectUri: string;
  responseType: string;
  state?: string;
  scope?: string;
  codeChallenge?: string;
  codeChallengeMethod?: string;
  resource?: string;
}

interface DenyOAuth2Params {
  redirectUri: string;
  state?: string;
}

interface OAuth2Response {
  redirectUrl: string;
}

export function useApproveOAuth2() {
  return useMutation<OAuth2Response, Error, ApproveOAuth2Params>({
    mutationFn: async (params) => {
      const searchParams = new URLSearchParams({
        client_id: params.clientId,
        redirect_uri: params.redirectUri,
        response_type: params.responseType,
        ...(params.state && { state: params.state }),
        ...(params.scope && { scope: params.scope }),
        ...(params.codeChallenge && { code_challenge: params.codeChallenge }),
        ...(params.codeChallengeMethod && {
          code_challenge_method: params.codeChallengeMethod,
        }),
        ...(params.resource && { resource: params.resource }),
      });

      const response = await apiFetch(
        `/oauth2/authorize?${searchParams.toString()}`,
        {
          method: 'POST',
          body: JSON.stringify({ approved: true }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to approve OAuth2 request');
      }

      const data = await response.json();
      return { redirectUrl: data.location };
    },
  });
}

export function useDenyOAuth2() {
  return useMutation<OAuth2Response, Error, DenyOAuth2Params>({
    mutationFn: async (params) => {
      const errorParams = new URLSearchParams({
        error: 'access_denied',
        error_description: 'The user denied the authorization request',
        ...(params.state && { state: params.state }),
      });

      const redirectUrl = `${params.redirectUri}?${errorParams.toString()}`;

      return { redirectUrl };
    },
  });
}
